// Pool coping takeoff engine — browser mirror of lib/pool_coping.py.
// Keep the two in lockstep; tests/test_pool_coping_v2.py runs shared vectors.
// Lengths are WATER-SIDE coping edge in inches. Runs are walked clockwise
// (water on the right); the turn at the END of a run sets the corner there.
// Names follow the standing ruling (inside bullnose concave, outside convex):
//   right turn = a regular pool corner = INSIDE corner, water in its 10 x 10 notch;
//   left turn  = where the deck juts into the pool = OUTSIDE corner, water wraps it.
(function (root) {
  const STRAIGHT_IN = 24, DEPTH_IN = 14, EPS = 1e-6;
  const JOINT_IN = 3 / 25.4;      // 3 mm joint between every two pieces, corners included (Eugene, 2026-10-01)
  const JOINT_OPEN_IN = 1 / 25.4; // joints never go under 3 mm; an edge may open them evenly to 4 mm to avoid a cut (Eugene, 2026-10-01)
  const MIN_CUT_IN = 8;           // a third of a piece: shorter would not hold over the water, so split the leftover over two pieces
  const LEG_IN = { inside: 10, outside: 24 }; // water edge each corner covers per leg
  const corner = t => (t === 'R' ? 'inside' : 'outside');

  function closure(runs) {
    let x = 0, y = 0, dx = 1, dy = 0;
    for (const r of runs) {
      x += dx * r.length_in; y += dy * r.length_in;
      [dx, dy] = r.turn === 'R' ? [-dy, dx] : [dy, -dx];
    }
    const gap = Math.hypot(x, y);
    return { closes: gap < 0.5 && dx === 1 && dy === 0, gap };
  }

  // Fill the straight space S between two corner pieces. n straights sit in n + 1 joints.
  // Returns the pieces in laying order, the joint actually used, and how much shorter or
  // longer the edge would have to be for no cut at all. `style` overrides the cut layout:
  // 'single' = one cut piece however small, 'balanced' = two equal cuts by the corners,
  // null = recommended (one piece unless it would be under MIN_CUT_IN).
  function fillRun(S, joint = JOINT_IN, style = null) {
    const step = STRAIGHT_IN + joint;
    let k = Math.max(0, Math.floor((S - joint) / step + EPS));        // whole pieces that fit
    const left = S - (k * STRAIGHT_IN + (k + 1) * joint);           // space after them, joints included
    let order, cuts = [], cut = null, used_style = null, single = null, balanced = null, auto = null;
    if (left <= (k + 1) * JOINT_OPEN_IN + EPS) order = Array(k).fill(STRAIGHT_IN);                  // joints open evenly, at most 1 mm
    else {
      const c = left - joint;                                          // one more piece, one more joint
      single = c; balanced = k >= 1 ? (STRAIGHT_IN + c) / 2 : null;
      auto = c >= MIN_CUT_IN || k === 0 ? 'single' : 'balanced';
      used_style = style === 'single' || (style === 'balanced' && k >= 1) ? style : auto;
      if (used_style === 'single') {                                   // one cut, in the middle of the edge
        cut = c; cuts = [c]; const h = Math.floor(k / 2);
        order = [...Array(h).fill(STRAIGHT_IN), c, ...Array(k - h).fill(STRAIGHT_IN)];
      } else {                                                         // a sliver: two equal cuts, one by each corner
        cut = c; const half = (STRAIGHT_IN + c) / 2; cuts = [half, half];
        order = [half, ...Array(k - 1).fill(STRAIGHT_IN), half];
      }
    }
    const used = order.reduce((t, v) => t + v, 0), jointUsed = order.length ? (S - used) / (order.length + 1) : S;
    const fits = !cuts.length;
    return { order, cuts, cut, pieces: order.length, full: order.length - cuts.length, joint_in: jointUsed,
      shorter_in: fits ? 0 : left, longer_in: fits ? 0 : step - left, style: used_style, auto_style: auto,
      single_in: single, balanced_in: balanced };
  }

  function takeoff(runs, allowancePct = 5, cutStyle = null) {
    const n = runs.length, out = [], problems = [];
    runs.forEach((r, i) => {
      const start = corner(runs[(i - 1 + n) % n].turn), end = corner(r.turn);
      const straight = r.length_in - LEG_IN[start] - LEG_IN[end];
      if (straight < -EPS) {
        const msg = `Run ${i + 1} is ${fmtIn(r.length_in)} but its ${start} and ${end} corner pieces need ${fmtIn(LEG_IN[start] + LEG_IN[end])}.`;
        problems.push(msg);
        out.push({ index: i + 1, length_in: r.length_in, start, end, straight_run_in: straight, pieces: 0, full_pieces: 0, cut_in: null, cuts: [], order: [], joint_in: JOINT_IN, shorter_in: null, longer_in: null, problem: msg });
        return;
      }
      const f = fillRun(straight, JOINT_IN, r.cut || cutStyle || null);
      out.push({ index: i + 1, length_in: r.length_in, start, end, straight_run_in: straight, pieces: f.pieces,
        full_pieces: f.full, cut_in: f.cut == null ? null : Math.round(f.cut * 100) / 100,
        cuts: f.cuts.map(c => Math.round(c * 100) / 100), order: f.order, joint_in: f.joint_in,
        shorter_in: f.shorter_in, longer_in: f.longer_in, style: f.style, auto_style: f.auto_style,
        single_in: f.single_in, balanced_in: f.balanced_in, problem: null });
    });
    let closes = false, gap = 0;
    if (n >= 4) {
      ({ closes, gap } = closure(runs));
      if (!closes) problems.push(`Runs do not close: ${gap.toFixed(1)} in. gap. Check lengths and turns.`);
    }
    const inside = runs.filter(r => r.turn === 'R').length, outside = n - inside;
    if (n >= 4 && inside - outside !== 4) problems.push('Walk the pool clockwise: right turns must exceed left turns by four.');
    const warnings = out.filter(r => r.cut_in != null && r.full_pieces === 0 && r.cut_in < MIN_CUT_IN)
      .map(r => `Run ${r.index} leaves a ${+r.cut_in.toFixed(2)} in. sliver between its corners. Change it by a few inches or plan a custom piece.`);
    const small = out.filter(r => r.style === 'single' && r.full_pieces > 0 && r.cut_in < MIN_CUT_IN);
    if (small.length) warnings.push(`One small piece on ${small.map(r => `edge ${r.index} (${fmtCut(r.cut_in)})`).join(', ')}. Pieces under ${MIN_CUT_IN}″ can crack when cut or come loose over the water.`);
    const straights = out.reduce((s, r) => s + r.pieces, 0);
    const extra = straights ? Math.ceil(straights * allowancePct / 100 - EPS) : 0;
    const perimeter_in = runs.reduce((s, r) => s + r.length_in, 0);
    const cuts = out.reduce((t, r) => t + r.cuts.length, 0); // cut pieces, not edges
    return { runs: out, perimeter_in, outside, inside, straights, allowance_pct: allowancePct, extra,
      straights_total: straights + extra, cuts, closes, closure_gap_in: gap, problems, warnings };
  }

  function quick(linearFt, outside, inside, allowancePct = 5) {
    // Closed pool: straights + corners pieces sit in as many joints. Pools the run, so offcuts are reused.
    const straight = linearFt * 12 - 2 * LEG_IN.outside * outside - 2 * LEG_IN.inside * inside;
    const straights = Math.max(0, Math.ceil((straight - (outside + inside) * JOINT_IN) / (STRAIGHT_IN + JOINT_IN) - EPS));
    const extra = straights ? Math.ceil(straights * allowancePct / 100 - EPS) : 0;
    return { straight_run_in: straight, straights, extra, straights_total: straights + extra, outside, inside };
  }

  const rectangle = (a, b) => [{ length_in: a, turn: 'R' }, { length_in: b, turn: 'R' }, { length_in: a, turn: 'R' }, { length_in: b, turn: 'R' }];
  const lShape = (a, b, c, d) => [
    { length_in: a, turn: 'R' }, { length_in: b - d, turn: 'R' }, { length_in: c, turn: 'L' },
    { length_in: d, turn: 'R' }, { length_in: a - c, turn: 'R' }, { length_in: b, turn: 'R' }];

  // Feet and inches to the nearest 1/8 in., written the way the trade writes it: 31′ 9⅞″.
  const EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
  const inchText = v => { let w = Math.floor(v + EPS), e = Math.round((v - w) * 8); if (e === 8) { w += 1; e = 0; } return `${w || !e ? w : ''}${EIGHTHS[e]}`; };
  function fmtIn(inches) {
    const neg = inches < 0, v = Math.abs(inches);
    let ft = Math.floor(v / 12 + EPS), rem = Math.round((v - ft * 12) * 8) / 8;
    if (rem >= 12) { ft += 1; rem -= 12; }
    const s = ft ? (rem ? `${ft}′ ${inchText(rem)}″` : `${ft}′`) : `${inchText(rem)}″`;
    return neg ? `−${s}` : s;
  }

  // Piece-by-piece plan in inches (y down). Water polygon + every coping unit
  // as a polygon: corner L's from the drawing envelope, 24 × 14 straights,
  // and the one cut straight per run (split into two balanced cuts when the
  // remainder is under 12 in., as an installer would lay it).
  function layout(runs, result) {
    const n = runs.length, verts = [], dirs = [];
    let x = 0, y = 0, dx = 1, dy = 0;
    for (const r of runs) {
      verts.push([x, y]); dirs.push([dx, dy]);
      x += dx * r.length_in; y += dy * r.length_in;
      [dx, dy] = r.turn === 'R' ? [-dy, dx] : [dy, -dx];
    }
    const add = (p, v, k) => [p[0] + v[0] * k, p[1] + v[1] * k];
    const deck = d => [d[1], -d[0]]; // outward (deck-side) normal, left of travel
    const pieces = [];
    for (let i = 0; i < n; i++) {
      const V = verts[i], uIn = dirs[(i - 1 + n) % n], uOut = dirs[i], nIn = deck(uIn), nOut = deck(uOut);
      const type = corner(runs[(i - 1 + n) % n].turn);
      const pts = type === 'inside' // water in the notch
        ? [add(V, uIn, -10), add(add(V, uIn, -10), nIn, 14), add(add(V, nIn, 14), nOut, 14), add(add(V, uOut, 10), nOut, 14), add(V, uOut, 10), V]
        : [V, add(V, uIn, -24), add(add(V, uIn, -24), nIn, 14), add(add(V, nIn, 14), nOut, 14), add(add(V, uOut, 24), nOut, 14), add(V, uOut, 24)];
      const leg = type === 'inside' ? 10 : 24; // water edge this corner covers per leg
      pieces.push({ kind: type, vertex: i, pts, label: add(add(V, nIn, 7), nOut, 7),
        outer: add(add(V, nIn, 14), nOut, 14), diag: [nIn[0] + nOut[0], nIn[1] + nOut[1]],
        water: [[add(V, uIn, -leg), V, nIn], [V, add(V, uOut, leg), nOut]] });
    }
    result.runs.forEach((rr, i) => {
      if (rr.problem || !rr.pieces) return;
      const u = dirs[i], nv = deck(u);
      let s = LEG_IN[rr.start] + rr.joint_in;
      rr.order.forEach(len => {
        const a = add(verts[i], u, s), b = add(verts[i], u, s + len);
        const cut = len < STRAIGHT_IN - EPS;
        pieces.push({ kind: cut ? 'cut' : 'straight', run: i, length_in: len, pair: cut && rr.cuts.length === 2 ? rr.cut_in : null, pts: [a, b, add(b, nv, DEPTH_IN), add(a, nv, DEPTH_IN)], water: [[a, b, nv]] });
        s += len + rr.joint_in;
      });
    });
    return { verts, dirs, pieces };
  }

  // Solve the last two runs so a custom shape closes (they are perpendicular
  // in any rectilinear walk). Returns new runs, or null when the turns can't close.
  function autoClose(runs) {
    const n = runs.length;
    if (n < 4) return null;
    let x = 0, y = 0, dx = 1, dy = 0; const heads = [];
    runs.forEach((r, i) => {
      heads.push([dx, dy]);
      if (i < n - 2) { x += dx * r.length_in; y += dy * r.length_in; }
      [dx, dy] = r.turn === 'R' ? [-dy, dx] : [dy, -dx];
    });
    if (!(dx === 1 && dy === 0)) return null;
    const h1 = heads[n - 2], h2 = heads[n - 1];
    const a = -(x * h1[0] + y * h1[1]), b = -(x * h2[0] + y * h2[1]);
    if (a <= 0 || b <= 0) return null;
    return runs.map((r, i) => (i === n - 2 ? { ...r, length_in: a } : i === n - 1 ? { ...r, length_in: b } : r));
  }

  // Add a 58 in. step in the middle of run i: one inside and one outside corner,
  // with room for exactly one straight between them (no cut). It steps out toward
  // the deck when the run ends in a regular (inside) corner and in toward the water
  // otherwise; either way the next run grows and the pool closes.
  // Returns null when the run is too short to hold a step with standard pieces.
  function addStep(runs, i, stepIn = 58) {
    const n = runs.length, r = runs[i], jog = r.turn === 'R' ? ['L', 'R'] : ['R', 'L'];
    const leg = t => LEG_IN[corner(t)];
    const min1 = leg(runs[(i - 1 + n) % n].turn) + leg(jog[0]), min2 = leg(jog[1]) + leg(r.turn);
    if (r.length_in < min1 + min2) return null;
    const half = Math.min(Math.max(Math.round(r.length_in / 2), min1), r.length_in - min2); // off-centre only if needed
    const out = runs.slice(0, i).concat([
      { length_in: half, turn: jog[0] }, { length_in: stepIn, turn: jog[1] }, { length_in: r.length_in - half, turn: r.turn }],
      runs.slice(i + 1));
    const next = (i + 3) % out.length;
    out[next] = { ...out[next], length_in: out[next].length_in + stepIn };
    return out;
  }

  // Phone shorthand: "32 R 16 R 32 R 16" (feet; 32'6", 32-6 and 32.5 all work).
  // A missing final turn is taken as R. Returns runs or throws with the bad token.
  function parseShorthand(text) {
    const tokens = String(text).trim().toUpperCase().replace(/[,;]/g, ' ').split(/\s+/).filter(Boolean);
    const runs = [];
    let i = 0;
    while (i < tokens.length) {
      const m = tokens[i].match(/^(\d+(?:\.\d+)?)(?:['′]|FT)?(?:-?(\d+(?:\.\d+)?)(?:["″]|IN)?)?$/);
      if (!m) throw new Error(`Can't read "${tokens[i]}" as a length`);
      let len = parseFloat(m[1]) * 12 + (m[2] ? parseFloat(m[2]) : 0);
      let turn = 'R';
      if (tokens[i + 1] === 'R' || tokens[i + 1] === 'L') { turn = tokens[i + 1]; i += 2; } else i += 1;
      runs.push({ length_in: len, turn });
    }
    if (runs.length < 4) throw new Error('Enter at least four runs');
    return runs;
  }
  const toShorthand = runs => runs.map((r, i) => {
    const ft = Math.floor(r.length_in / 12 + EPS), inch = +(r.length_in - ft * 12).toFixed(2);
    return (inch ? `${ft}-${inch}` : `${ft}`) + (i < runs.length - 1 ? ` ${r.turn}` : r.turn === 'R' ? '' : ' L');
  }).join(' ');

  const fmtCut = v => `${inchText(v)}″`;
  // T-shape: an A-wide bar D deep, with a C-wide stem centred under it; B overall.
  const tShape = (a, b, c, d) => {
    const side = (a - c) / 2;
    return [
      { length_in: a, turn: 'R' }, { length_in: d, turn: 'R' }, { length_in: side, turn: 'L' },
      { length_in: b - d, turn: 'R' }, { length_in: c, turn: 'R' }, { length_in: b - d, turn: 'L' },
      { length_in: side, turn: 'R' }, { length_in: d, turn: 'R' }];
  };

  const headings = runs => { let dx = 1, dy = 0; return runs.map(r => { const h = [dx, dy]; [dx, dy] = r.turn === 'R' ? [-dy, dx] : [dy, -dx]; return h; }); };
  const dot = (a, b) => a[0] * b[0] + a[1] * b[1];

  // Drag edge i by `delta` inches toward the deck (negative = into the water).
  // The edge keeps its length; its two neighbours stretch, so the pool stays closed.
  // Returns new runs, or null if a neighbour would vanish.
  function moveEdge(runs, i, delta) {
    const n = runs.length, H = headings(runs), nrm = [H[i][1], -H[i][0]];
    const p = (i - 1 + n) % n, q = (i + 1) % n, out = runs.map(r => ({ ...r }));
    out[p].length_in += delta * dot(H[p], nrm);
    out[q].length_in -= delta * dot(H[q], nrm);
    return out[p].length_in > 0.5 && out[q].length_in > 0.5 ? out : null;
  }

  // Set run i to `length` by moving one of its neighbouring edges. Prefers a move that runs
  // no edge into another (crosses()), then an edge inside the outline so the overall size
  // holds (widening an L's cut-out keeps A); on a tie, the move that disturbs the longer parallel run.
  function setRunLength(runs, i, length) {
    const n = runs.length, H = headings(runs), delta = length - runs[i].length_in;
    if (Math.abs(delta) < EPS) return runs;
    const { verts } = layout(runs, { runs: [] });
    const xs = verts.map(v => v[0]), ys = verts.map(v => v[1]);
    const box = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    const onBox = j => { const a = verts[j], b = verts[(j + 1) % n];
      return (Math.abs(a[0] - b[0]) < EPS && (Math.abs(a[0] - box[0]) < EPS || Math.abs(a[0] - box[2]) < EPS)) ||
             (Math.abs(a[1] - b[1]) < EPS && (Math.abs(a[1] - box[1]) < EPS || Math.abs(a[1] - box[3]) < EPS)); };
    const next = (i + 1) % n, prev = (i - 1 + n) % n;
    const options = [next, prev].map(j => {
      // moving edge `next` stretches run i as its previous neighbour; `prev` as its next
      const s = dot(H[i], [H[j][1], -H[j][0]]) * (j === next ? 1 : -1);
      const far = j === next ? (i + 2) % n : (i - 2 + n) % n; // the other run the move stretches
      return { moved: moveEdge(runs, j, delta / s), inside: !onBox(j), far: runs[far].length_in };
    }).filter(o => o.moved && Math.abs(o.moved[i].length_in - length) < 0.01);
    if (!options.length) return null;
    const clear = !crosses(runs); // a pool already running into itself: no preference
    options.forEach(o => { o.clear = clear && !crosses(o.moved); });
    options.sort((a, b) => (b.clear - a.clear) || (b.inside - a.inside) || (b.far - a.far));
    return options[0].moved;
  }

  // ── dragging an edge (Eugene 2026-10-02), one helper for v3 and v4. Pure drawing geometry: no piece math changes. ──
  // The water edge an edge needs for the corner pieces at both its ends (inside-inside 20, inside-outside 34, outside-outside 48 in.).
  const cornerMin = (runs, j) => { const n = runs.length; return LEG_IN[corner(runs[(j - 1 + n) % n].turn)] + LEG_IN[corner(runs[j].turn)]; };
  // A jog: the turns at an edge's two ends differ (one inside, one outside corner), so the edges either side of it run
  // the same way, a step apart. It is a step's riser: at length 0 those two edges meet in line.
  const isJog = (runs, j) => runs[(j - 1 + runs.length) % runs.length].turn !== runs[j].turn;
  // moveEdge()'s arithmetic without its floor: the previous run grows by d·(h_prev·n), the next run shrinks by d·(h_next·n).
  function stretch(runs, k, d) {
    const n = runs.length, H = headings(runs), nrm = [H[k][1], -H[k][0]], p = (k - 1 + n) % n, q = (k + 1) % n, out = runs.map(r => ({ ...r }));
    out[p].length_in += d * dot(H[p], nrm); out[q].length_in -= d * dot(H[q], nrm);
    return out;
  }
  // ── keeping edges apart (QA 2026-10-02): no drag, typed length or drawn step runs one edge into another. ──
  // What each edge takes up on the plan, as boxes [x0, y0, x1, y1]: its water line, and its coping band: DEPTH_IN deep on
  // the deck side, corner to corner, and on past an inside corner by DEPTH_IN (that corner piece's outer square).
  const GAP_IN = 1; // a drag stops this far short of two water lines meeting; coping may butt up to coping
  const bbox = ps => [Math.min(...ps.map(p => p[0])), Math.min(...ps.map(p => p[1])), Math.max(...ps.map(p => p[0])), Math.max(...ps.map(p => p[1]))];
  function footprints(runs) {
    const n = runs.length, H = headings(runs); let x = 0, y = 0;
    return runs.map((r, i) => {
      const [dx, dy] = H[i], nv = [dy, -dx], a = [x, y], b = [x + dx * r.length_in, y + dy * r.length_in];
      const s = runs[(i - 1 + n) % n].turn === 'R' ? DEPTH_IN : 0, e = r.turn === 'R' ? DEPTH_IN : 0;
      const a2 = [a[0] - dx * s, a[1] - dy * s], b2 = [b[0] + dx * e, b[1] + dy * e];
      [x, y] = b;
      return { line: bbox([a, b]), band: bbox([a2, b2, [a2[0] + nv[0] * DEPTH_IN, a2[1] + nv[1] * DEPTH_IN], [b2[0] + nv[0] * DEPTH_IN, b2[1] + nv[1] * DEPTH_IN]]) };
    });
  }
  // Water lines meet when they touch; coping bands when they overlap (butting up is fine).
  const meets = (P, Q, open) => open
    ? P[0] < Q[2] - EPS && Q[0] < P[2] - EPS && P[1] < Q[3] - EPS && Q[1] < P[3] - EPS
    : P[0] <= Q[2] + EPS && Q[0] <= P[2] + EPS && P[1] <= Q[3] + EPS && Q[1] <= P[3] + EPS;
  const KEYS = [['line', false], ['band', true]];
  // The pairs of edges that aren't joined to each other (n ≤ 64, so every pair).
  const apart = n => { const ps = []; for (let a = 0; a < n; a++) for (let b = a + 2; b < n; b++) if (!(a === 0 && b === n - 1)) ps.push([a, b]); return ps; };
  // The first two edges, not joined to each other, that run into each other: their water lines touch or cross, or
  // their coping overlaps. { a, b, coping } (a < b; coping: only the coping meets), or null for a clear outline.
  function crosses(runs) {
    const F = footprints(runs);
    for (const [a, b] of apart(runs.length)) for (const [key, open] of KEYS) if (meets(F[a][key], F[b][key], open)) return { a, b, coping: open };
    return null;
  }
  // How far edge k can be dragged each way (stretch(), d inches) before an edge runs into one it isn't joined to: the
  // dragged edge, or a neighbour as it stretches, meeting another edge. Pairs that already meet at d = 0 are left as
  // they are. { lo, hi }: each { d, edge: the edge run into, by: the edge that runs into it, coping } or null.
  function clearance(runs0, k) {
    const n = runs0.length, H = headings(runs0), F0 = footprints(runs0), pairs = apart(n), moving = new Set([(k - 1 + n) % n, k, (k + 1) % n]), room = {};
    for (const s of [1, -1]) {
      const F1 = footprints(stretch(runs0, k, s / 8)); // each side of a box moves 0 or 1 in. per inch dragged
      let best = null;
      for (const [a, b] of pairs) for (const [key, open] of KEYS) {
        if (meets(F0[a][key], F0[b][key], open)) continue;
        // they meet while, on both axes, each box's low side is under (lines: at most at) the other's high side:
        // c + m·t < 0 (≤ 0), t = |d|; that holds from t0 to t1
        let t0 = 0, t1 = Infinity;
        for (const [P, Q] of [[a, b], [b, a]]) for (const ax of [0, 1]) {
          const c = F0[P][key][ax] - F0[Q][key][ax + 2], m = Math.round(((F1[P][key][ax] - F1[Q][key][ax + 2]) - c) * 8);
          if (m > 0) t1 = Math.min(t1, -c / m); else if (m < 0) t0 = Math.max(t0, -c / m); else if (open ? c > -EPS : c > EPS) t1 = -1;
        }
        if (open ? !(t0 < t1 - EPS) : !(t0 <= t1 + EPS)) continue; // they never meet (coping only butting up for an instant is fine)
        const t = open ? t0 : Math.max(0, t0 - GAP_IN), by = moving.has(a) ? a : b, edge = by === a ? b : a, par = dot(H[a], H[b]) !== 0;
        // the nearest; at a tie (an edge meeting a corner), the edge it runs along, which is what it is seen to hit
        if (!best || t < best.t - EPS || (t < best.t + EPS && par && !best.par)) best = { t, edge, by, coping: open, par };
      }
      room[s > 0 ? 'hi' : 'lo'] = best && { d: s * best.t, edge: best.edge, by: best.by, coping: best.coping };
    }
    return room;
  }
  // Take the zero-length jogs `zero` out of a walk: the two edges either side of each run in line, so they join into one
  // (lengths added, the later one's turn, no cut choice of its own). The walk then starts on the edge that starts where
  // the old walk did, else on one that runs the same way, so the drawing keeps its orientation; `origin` is where the new
  // first edge starts in the old drawing. at(i): old edge i's index in the new walk (a removed jog: the edge it went into).
  function flattenJogs(runs, zero) {
    const n = runs.length, H = headings(runs), out = runs.map(r => ({ ...r })), alive = runs.map(() => true);
    const into = runs.map((_, i) => i), from = runs.map((_, i) => i); // into: the edge it joined; from: a joined edge's first old edge
    const near = (i, s) => { let j = (i + s + n) % n; while (!alive[j]) j = (j + s + n) % n; return j; };
    for (const j of zero) {
      const a = near(j, -1), b = near(j, 1), { cut, ...keep } = out[b];
      alive[j] = alive[a] = false; into[j] = into[a] = b; from[b] = from[a];
      out[b] = { ...keep, length_in: out[a].length_in + out[j].length_in + out[b].length_in };
    }
    const verts = []; let x = 0, y = 0;
    runs.forEach((r, i) => { verts.push([x, y]); x += H[i][0] * r.length_in; y += H[i][1] * r.length_in; });
    const live = [...Array(n).keys()].filter(i => alive[i]), east = live.filter(i => H[i][0] === 1 && H[i][1] === 0);
    const s = east.find(i => Math.hypot(verts[from[i]][0], verts[from[i]][1]) < EPS) ?? east[0] ?? live[0];
    const order = live.slice(live.indexOf(s)).concat(live.slice(0, live.indexOf(s))), pos = new Map(order.map((i, t) => [i, t]));
    const at = i => { while (!alive[i]) i = into[i]; return pos.get(i); };
    return { runs: order.map(i => out[i]), at, origin: verts[from[s]] };
  }
  // Drag edge k by `raw` inches toward the deck (the pointer's travel along the edge's normal; negative = into the water).
  // Its two neighbours stretch, as in moveEdge(). opts: { ppi: screen px per inch at the drag's start, touch, free (Alt or
  // Option held: no snap), prev (the previous call's result in this drag), maxEdge }.
  //  1. Snap: near a length where a moving neighbour takes whole pieces only, the edge lands there. The pull is 9 px (14
  //     for a finger) at the drag's scale, at most 6 in., whether one or both neighbours come out whole; both win, then
  //     the nearest. It lands on a 1/8 in. size the tape reads when the joints allow, never on a pool with more problems.
  //  2. Hysteresis: a snap holds while the pointer stays within 1.6 × the pull of it, so it doesn't flicker.
  //  3. Between snaps the edge moves in whole inches.
  //  4. Overlap guard, for a neighbour that had room for its corners when the drag started: it never gets shorter than its
  //     corners need. A step's riser (a jog) pushed under half that flattens instead: it goes to 0 and comes out, and the
  //     two edges either side of it join into one (both corners go). A riser between half and its minimum, and any other
  //     edge, stops at the minimum. No edge goes past maxEdge; the pool always closes.
  //  5. Clearance (QA 2026-10-02): the edge, and each neighbour as it stretches, stops before it runs into an edge it isn't
  //     joined to (clearance()): GAP_IN short of the water lines meeting, or where the coping would start to overlap. A step
  //     only comes out when the pool it leaves runs no edge into another.
  // Returns { runs, delta, snapped, whole: [neighbours with no cut because of the snap], clamped: null | { reason: 'corners',
  // edge, minIn, jog, edges: [every neighbour at its minimum], minsIn: [their minimums], steps: how many of those are risers
  // that can come out } | { reason: 'cross', edge: the edge it would run into, by: the edge that would (the dragged one or
  // a neighbour), coping: true when only the coping would meet },
  // merged: null | { removed: [risers taken out, as indexes in runs0], into: the joined edge in runs }, kIndex: the dragged
  // edge in runs, origin: where runs' first vertex sits in runs0's drawing }. A drawing shifted by `origin` keeps every
  // corner where it was but the dragged edge's two: that edge follows the pointer, even when it is E1 or the last edge,
  // whose end is the walk's first vertex, and when a step at the start of the walk came out.
  function dragEdge(runs0, k, raw, opts = {}) {
    const n = runs0.length, H = headings(runs0), nrm = [H[k][1], -H[k][0]], prev = opts.prev || null;
    const maxEdge = opts.maxEdge > 0 ? opts.maxEdge : Infinity;
    const pull = Math.min(6, (opts.touch ? 14 : 9) / (opts.ppi > 0 ? opts.ppi : 1)), hold = 1.6 * pull;
    const nb = [[(k - 1 + n) % n, dot(H[(k - 1 + n) % n], nrm)], [(k + 1) % n, -dot(H[(k + 1) % n], nrm)]].map(([j, sg]) => {
      const L0 = runs0[j].length_in, min = cornerMin(runs0, j), valid = L0 >= min - EPS;
      return { j, sg, L0, min, valid, jog: isJog(runs0, j), lo: valid ? Math.min(min, L0) : Math.min(L0, 1), hi: Math.max(L0, maxEdge) };
    });
    const len = (b, d) => b.L0 + b.sg * d;
    const anchor = d => (k === 0 || k === n - 1 ? [d * nrm[0], d * nrm[1]] : [0, 0]); // the walk's first vertex moves with edges E1 and En
    // where the edge may go: each neighbour between its floor and maxEdge (d = 0, where it started, is always inside)
    let lo = { d: -Infinity }, hi = { d: Infinity };
    for (const b of nb) {
      const toMin = { d: (b.lo - b.L0) / b.sg, b, min: b.valid }, toMax = { d: (b.hi - b.L0) / b.sg, b };
      const [a, z] = b.sg > 0 ? [toMin, toMax] : [toMax, toMin];
      if (a.d > lo.d) lo = a;
      if (z.d < hi.d) hi = z;
    }
    // 5. nor may it run into an edge it isn't joined to
    const room = clearance(runs0, k);
    if (room.lo && room.lo.d > lo.d) lo = { d: room.lo.d, cross: room.lo };
    if (room.hi && room.hi.d < hi.d) hi = { d: room.hi.d, cross: room.hi };
    const inRange = d => d >= lo.d - EPS && d <= hi.d + EPS;
    // 4. riser b taken out: the result, or null when it can't come out here (the joined edge would be longer than one
    // edge can be, or the pool would run an edge into another, unless it already did)
    let clean;
    const flatten = b => {
      if (!b.valid || !b.jog || n < 6) return null;
      const d = -b.L0 / b.sg, zero = nb.filter(o => o.valid && o.jog && Math.abs(len(o, d)) < 1e-6).map(o => o.j);
      if (n - 2 * zero.length < 4 || !nb.every(o => zero.includes(o.j) || (len(o, d) >= o.lo - EPS && len(o, d) <= o.hi + EPS))) return null;
      const moved = stretch(runs0, k, d); zero.forEach(j => { moved[j].length_in = 0; });
      const F = flattenJogs(moved, zero), into = F.at(zero[0]);
      if (!closure(F.runs).closes || F.runs[into].length_in > maxEdge) return null; // the joined edge, too, no longer than one edge can be
      if (clean === undefined) clean = !crosses(runs0);
      if (clean && crosses(F.runs)) return null;
      return { runs: F.runs, delta: d, snapped: false, whole: [], clamped: null, merged: { removed: zero, into }, kIndex: F.at(k), origin: [anchor(d)[0] + F.origin[0], anchor(d)[1] + F.origin[1]] };
    };
    // a riser pushed under half its minimum (once out, it stays out until the pointer is `hold` past half)
    let flat = null;
    for (const b of nb) {
      const out = prev && prev.merged && prev.merged.removed.includes(b.j);
      if (!b.valid || !b.jog || !(len(b, raw) < b.min / 2 + (out ? hold : 0))) continue;
      const f = flatten(b);
      if (f && (!flat || Math.abs(f.delta) < Math.abs(flat.delta))) flat = f;
    }
    if (flat) return flat;
    // 3. free: whole inches, kept in range; past a corner minimum or another edge it says which edge stopped it
    const r0 = Math.round(raw), free = Math.min(hi.d, Math.max(lo.d, r0)), pin = r0 < lo.d ? lo : r0 > hi.d ? hi : null;
    let d = free, snapped = false, whole = [];
    if (!opts.free) { // 1. and 2.
      const step = STRAIGHT_IN + JOINT_IN;
      const wholeAt = x => nb.filter(b => { const sp = len(b, x) - b.min; return sp > 0 && !fillRun(sp).cuts.length; }).map(b => b.j);
      const problems = x => takeoff(stretch(runs0, k, x), 0).problems.length, base = problems(free);
      const fits = x => inRange(x) && wholeAt(x).length > 0 && problems(x) <= base;
      let best = null, bestN = wholeAt(free).length;
      if (prev && prev.snapped && !prev.merged && Math.abs(prev.delta - raw) <= hold && fits(prev.delta)) best = prev.delta;
      else for (const b of nb) {
        const sp = len(b, raw) - b.min, m = Math.floor((sp - JOINT_IN) / step);
        for (let c = m - 1; c <= m + 2; c++) { // the whole-piece lengths round the pointer
          if (c < 0) continue;
          const least = c * STRAIGHT_IN + (c + 1) * JOINT_IN; // land on a size the labels and the tape read (1/8, else 1/16 in.), when the joints allow it
          const target = [8, 16].map(q => Math.ceil(least * q - 1e-9) / q).find(g => !fillRun(g).cuts.length) ?? least;
          const x = raw + (target - sp) / b.sg;
          if (Math.abs(x - raw) > pull || !fits(x)) continue;
          const got = wholeAt(x).length;
          if (got > bestN || (got === bestN && (best === null || Math.abs(x - raw) < Math.abs(best - raw)))) { best = x; bestN = got; }
        }
      }
      if (best !== null) { d = best; snapped = true; whole = wholeAt(best).sort((a, b) => a - b); }
    }
    let clamped = null;
    if (!snapped && pin && pin.cross) clamped = { reason: 'cross', edge: pin.cross.edge, by: pin.cross.by, coping: pin.cross.coping };
    else if (!snapped && pin && pin.min) { // every neighbour at its minimum there (both sides of a T's stem stop together)
      const hit = nb.filter(b => b.valid && Math.abs(len(b, d) - b.lo) < 1e-6).sort((a, b) => a.j - b.j), at = hit.length ? hit : [pin.b], out = at.filter(b => b.jog && flatten(b));
      clamped = { reason: 'corners', edge: pin.b.j, minIn: pin.b.min, jog: out.length > 0, edges: at.map(b => b.j), minsIn: at.map(b => b.min), steps: out.length };
    }
    return { runs: stretch(runs0, k, d), delta: d, snapped, whole, clamped, merged: null, kIndex: k, origin: anchor(d) };
  }

  const api = { fillRun, JOINT_IN, MIN_CUT_IN, tShape, moveEdge, setRunLength, headings, fmtCut, STRAIGHT_IN, DEPTH_IN, LEG_IN, takeoff, quick, closure, rectangle, lShape, fmtIn, layout, autoClose, addStep, parseShorthand, toShorthand,
    dragEdge, cornerMin, isJog, crosses };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.CopingTakeoff = api;
})(typeof window !== 'undefined' ? window : globalThis);
